import React from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import ZipHeatmap from './ZipHeatmap';
import StateChoropleth from './StateChoropleth';
import DmaChoropleth from './DmaChoropleth';
import { DMA_REGIONS } from '../../data/dmaList';
import { rangeQuery } from './reportRange';
import RangeBadge from './RangeBadge';
import { GEO_MAP_HEIGHT } from './reportTheme';

// Publica reports DMA as the numeric Nielsen code (e.g. "669"); map it to a
// readable market name for display, falling back to the code if unknown.
const DMA_NAME_BY_CODE: Record<string, string> = Object.fromEntries(
  DMA_REGIONS.map((d) => [d.code, d.name]),
);
const dmaLabel = (code: string): string => DMA_NAME_BY_CODE[code] || code;

// CTV-445: per-campaign geographic delivery. Data comes from the selfservice-api
// GET /submissions/:id/geo endpoint, which sums selfservice_campaign_geo_daily
// over the submission's flights and returns independent state / DMA / ZIP
// rollups. Completion rate is derived here (completes / impressions).

interface GeoResponse {
  total: { impressions: number; completes: number };
  byState: Array<{ state: string; impressions: number; completes: number }>;
  // `name` is the census_dmas market name (same source as the map); falls back
  // to the code when census has no row for it.
  byDma: Array<{ dma: string; name?: string | null; impressions: number; completes: number }>;
  byZip: Array<{ zip: string; impressions: number; completes: number }>;
}

// CTV-560: ZIP breakdown shows union(top N ZIPs, all ZIPs ≥ ZIP_OTHER_MIN). The
// bare-threshold rule used in CTV-445 buried every ZIP into "Other" on small
// campaigns where nothing crossed 100 impressions; bare top-N hid the long tail
// on big campaigns. The union gives small campaigns a visible top-of-tail and
// large campaigns the complete ≥100 list.
const ZIP_TOP_N = 25;
const ZIP_OTHER_MIN = 100;
// Row key for the aggregate "Other" row — intentionally matches no map feature.
const OTHER_KEY = '__other__';

// CTV-560: the panel-visible ZIP set — union of "top N by impressions" and "all
// ZIPs ≥ ZIP_OTHER_MIN". Shared by the breakdown list and the map so they show
// the same ZIPs.
function shownZipKeys(
  zips: Array<{ zip: string; impressions: number }>,
): Set<string> {
  const sorted = [...zips].sort((a, b) => b.impressions - a.impressions);
  const keys = new Set<string>();
  sorted.slice(0, ZIP_TOP_N).forEach((r) => keys.add(r.zip));
  sorted.filter((r) => r.impressions >= ZIP_OTHER_MIN).forEach((r) => keys.add(r.zip));
  return keys;
}

type Level = 'state' | 'dma' | 'zip';

const LEVELS: { key: Level; label: string }[] = [
  { key: 'state', label: 'State' },
  { key: 'dma', label: 'DMA' },
  { key: 'zip', label: 'ZIP' },
];

interface GeographicSectionProps {
  submissionId: string;
  accentColor?: string;
  secondaryColor?: string;
  from?: string | null;
  to?: string | null;
  rangeLabel?: string | null;
}

const GeographicSection: React.FC<GeographicSectionProps> = ({ submissionId, accentColor, secondaryColor, from, to, rangeLabel }) => {
  const accent = accentColor || '#0ea5e9';
  const marker = secondaryColor || accent;
  const [data, setData] = React.useState<GeoResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // CTV-445: default to ZIP so the heatmap is visible up front (the report page
  // pairs this with the delivery chart side-by-side).
  const [level, setLevel] = React.useState<Level>('zip');
  // Row hovered in the breakdown list — used to highlight the matching ZIP on
  // the map.
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
  // Hover-intent: only commit the highlight (which moves/zooms the map) after
  // the cursor rests on a row briefly, so brushing past rows doesn't yank the
  // map around. Leaving a row cancels a pending highlight immediately.
  const hoverTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const HOVER_INTENT_MS = 300;
  const onRowEnter = (key: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoveredKey(key), HOVER_INTENT_MS);
  };
  const onRowLeave = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setHoveredKey(null);
  };
  React.useEffect(() => () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    // Keep the current map + breakdown on screen (dimmed) while the new range
    // loads, rather than tearing the map down to a spinner on every press.
    setLoading(true);
    setError(null);
    apiClient
      .get<GeoResponse>(`/submissions/${submissionId}/geo${rangeQuery({ from: from ?? null, to: to ?? null })}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load geographic data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, from, to]);

  const rows = React.useMemo(() => {
    if (!data) return [] as Array<{ key: string; label: string; impressions: number; completes: number }>;
    // Tolerate a partial/malformed response — render the empty state rather
    // than throwing if a breakdown array is missing. `key` is the raw map key
    // (state code / DMA code / ZIP) used to highlight the matching region on the
    // map; `label` is the display text.
    if (level === 'state')
      return (data.byState ?? []).map((r) => ({ key: r.state, label: r.state, impressions: r.impressions, completes: r.completes }));
    if (level === 'dma')
      return (data.byDma ?? []).map((r) => ({ key: r.dma, label: r.name || dmaLabel(r.dma), impressions: r.impressions, completes: r.completes }));
    // ZIP: collapse the long tail into a single bottom "Other" row so the list
    // stays readable. It's a panel-only aggregate — the map still shows every
    // actual ZIP — so its key (OTHER_KEY) matches no map feature and hovering it
    // is a no-op.
    const zips = data.byZip ?? [];
    const sorted = [...zips].sort((a, b) => b.impressions - a.impressions);
    const keys = shownZipKeys(sorted);
    const shown = sorted
      .filter((r) => keys.has(r.zip))
      .map((r) => ({ key: r.zip, label: r.zip, impressions: r.impressions, completes: r.completes }));
    const tail = sorted.filter((r) => !keys.has(r.zip));
    if (tail.length > 0) {
      shown.push({
        key: OTHER_KEY,
        label: 'Other',
        impressions: tail.reduce((a, r) => a + r.impressions, 0),
        completes: tail.reduce((a, r) => a + r.completes, 0),
      });
    }
    return shown;
  }, [data, level]);

  // ZIPs shown on the map — same union set the panel lists individually, so the
  // map and the breakdown panel never disagree about which ZIPs are "visible."
  const mapZips = React.useMemo(() => {
    const zips = data?.byZip ?? [];
    const keys = shownZipKeys(zips);
    return zips.filter((r) => keys.has(r.zip));
  }, [data]);

  const sectionHeader = (
    <div className="flex items-center justify-between mb-2 px-1 gap-3">
      <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
        <span className="w-0.5 h-3 rounded-full" style={{ backgroundColor: marker }} />
        Geographic delivery
        <RangeBadge label={rangeLabel} accentColor={accent} />
      </div>
    </div>
  );

  // Spinner only on the very first load; range changes keep the prior map.
  if (data === null) {
    return (
      <div>
        {sectionHeader}
        <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-8 text-center text-[12px] text-slate-400">
          <Loader2 className="inline-block w-3 h-3 mr-1 animate-spin" />
          Loading geographic data…
        </div>
      </div>
    );
  }

  const totalImps = data?.total?.impressions ?? 0;
  if (error || !data || totalImps === 0) {
    return (
      <div>
        {sectionHeader}
        <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-8 text-center text-[13px] text-slate-600">
          {error ? `Couldn't load geographic data: ${error}` : 'No geographic delivery data yet.'}
        </div>
      </div>
    );
  }

  return (
    <div>
      {sectionHeader}
      <div
        className={`rounded-2xl border border-slate-200/80 bg-white overflow-hidden transition-opacity duration-300 ${loading ? 'opacity-60' : 'opacity-100'}`}
      >
        {/* Level toggle */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-200/70">
          <MapPin className="w-3.5 h-3.5 mr-1" style={{ color: accent }} />
          {LEVELS.map((l) => {
            const active = level === l.key;
            return (
              <button
                key={l.key}
                type="button"
                onClick={() => setLevel(l.key)}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium tracking-tight transition-colors whitespace-nowrap"
                style={
                  active
                    ? { backgroundColor: accent, color: '#fff' }
                    : { color: 'rgb(71, 85, 105)', backgroundColor: 'rgb(241, 245, 249)' }
                }
                aria-pressed={active}
              >
                {l.label}
              </button>
            );
          })}
        </div>

        {/* Full-width row: map on the left, breakdown bars on the right. The
            maps are a fixed height, so the bars column matches it and scrolls
            internally (1-row State/DMA vs many-row ZIP). */}
        <div className="lg:grid lg:grid-cols-2" style={{ ['--geo-h' as string]: `${GEO_MAP_HEIGHT}px` }}>
          {/* Map */}
          <div className="p-3 lg:border-r border-slate-200/70">
            {level === 'state' && data.byState.length > 0 && (
              <StateChoropleth byState={data.byState} accentColor={accent} highlightKey={hoveredKey} highlightColor={marker} />
            )}
            {level === 'dma' && data.byDma.length > 0 && (
              <DmaChoropleth byDma={data.byDma} accentColor={accent} highlightKey={hoveredKey} highlightColor={marker} />
            )}
            {/* ZIP-boundary heatmap — only ZIP has per-area boundaries; renders
                nothing if the map can't load (the bars still have the numbers). */}
            {level === 'zip' && mapZips.length > 0 && (
              <ZipHeatmap byZip={mapZips} accentColor={accent} highlightZip={hoveredKey} highlightColor={marker} />
            )}
          </div>

          {/* Breakdown bars — fill the column down to the legend. The map's
              fixed height drives the row; min-h-0 keeps ZIP's many rows from
              stretching it, so the scroll area fills the rest and scrolls. */}
          <div className="flex flex-col border-t lg:border-t-0 lg:min-h-0">
            {/* Column labels (header) */}
            <div className="px-5 py-2.5 bg-slate-50/60 border-b border-slate-200/70 text-[10px] uppercase tracking-[0.12em] text-slate-400 flex items-center gap-4">
              <span className="w-28 shrink-0">{LEVELS.find((l) => l.key === level)?.label}</span>
              <span className="flex-1">Share of plays</span>
              <span className="w-24 shrink-0 text-right">Plays</span>
              <span className="w-16 shrink-0 text-right">Compl.</span>
            </div>
            <div className="overflow-y-auto divide-y divide-slate-100 max-h-72 lg:max-h-none lg:h-[var(--geo-h)]">
              {rows.map((r) => {
                const rate = r.impressions > 0 ? Math.min(100, Math.round((r.completes / r.impressions) * 100)) : 0;
                const share = totalImps > 0 ? (r.impressions / totalImps) * 100 : 0;
                return (
                  <div
                    key={`${level}-${r.key}`}
                    className="px-5 py-3 flex items-center gap-4 transition-colors hover:bg-slate-50"
                    onMouseEnter={() => onRowEnter(r.key)}
                    onMouseLeave={onRowLeave}
                  >
                    <div className="w-28 shrink-0 text-[13px] font-medium text-slate-700 truncate" title={r.label}>
                      {r.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: accent }} />
                      </div>
                    </div>
                    <div className="w-24 shrink-0 text-right text-[13px] font-semibold tabular-nums text-slate-900">
                      {r.impressions.toLocaleString('en-US')}
                    </div>
                    <div className="w-16 shrink-0 text-right text-[12px] tabular-nums text-slate-500">
                      {rate}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeographicSection;
